import type { api } from '@vrplatform/data';
import type { party_enum } from '@vrplatform/graphql';
import type { EmotionSx } from '@mantine/emotion';
import { Text } from '@mantine/core';
import { formatPercentage } from '@vrplatform/ui-common';
import { day } from '@vrplatform/utils';
import { type ReactNode, useMemo } from 'react';
import { useEmbedViewMode } from '../../providers';
import { useProfitAndLossTableFilter } from './useProfitAndLossTableFilter';
import type { ProfitAndLossRow } from './useProfitAndLossTableQuery';

export type ProfitAndLossJournalEntriesArgs = {
  accountId?: string;
  categoryId?: string;
  filter?: api['/reports/journal-entries']['get']['parameters']['query'];
};

type ProfitAndLossOpenJournalEntries = (
  args: ProfitAndLossJournalEntriesArgs
) => void;

const Base = ({
  type,
  children,
  align,
  size = 'sm',
  percentage,
  breakWord = false,
  onClick,
}: {
  depth: number;
  children: ReactNode;
  align: 'left' | 'right';
  size?: 'md' | 'sm';
  percentage?: number;
  breakWord?: boolean;
  type: ProfitAndLossRow['type'];
  onClick?: () => void;
}) => {
  const sx = useMemo<EmotionSx>(() => {
    const breakWordStyle: EmotionSx | undefined = breakWord
      ? {
          display: 'block',
          wordBreak: 'break-all',
          whiteSpace: 'normal',
          lineBreak: 'anywhere',
        }
      : undefined;

    const onClickStyle: EmotionSx | undefined = onClick
      ? {
          cursor: 'pointer',
          transition: 'text-decoration 0.2s ease-in-out',
          ':hover': {
            textDecoration: 'underline',
          },
        }
      : undefined;
    return {
      ...breakWordStyle,
      ...onClickStyle,
    };
  }, [breakWord, onClick]);

  return (
    <Text
      fw={type === 'group' ? 'bold' : 'normal'}
      size={size}
      ta={align}
      sx={sx}
      onClick={onClick}
    >
      {typeof percentage === 'number' && (
        <>
          <Text span size="xs" fw={400} mr="xs" c="neutral">
            ({formatPercentage(percentage / 100, 'legacy', 2)})
          </Text>{' '}
        </>
      )}
      {children}
    </Text>
  );
};

const Number = ({
  children,
  columndId,
  depth,
  onOpenJournalEntries,
  row,
}: {
  row: ProfitAndLossRow;
  columndId: string;
  children: ReactNode;
  depth: number;
  onOpenJournalEntries?: ProfitAndLossOpenJournalEntries;
}) => {
  const { isEmbed } = useEmbedViewMode();
  const {
    filter: { type: view, startDate: globalStartDate, endDate: globalEndDate },
  } = useProfitAndLossTableFilter();

  const categoryId = row.type === 'category' ? row.id : undefined;
  const accountId = row.type === 'account' ? row.id : undefined;

  const onClick = useMemo<(() => void) | undefined>(() => {
    if (isEmbed || !onOpenJournalEntries || (!accountId && !categoryId)) {
      return undefined;
    }

    const base: api['/reports/journal-entries']['get']['parameters']['query'] =
      {
        accountIds: accountId,
        categoryIds: categoryId,
      };

    if (view === 'listing') {
      base.listingIds = columndId;
    }
    if (view === 'party') {
      base.party = columndId as party_enum;
    }
    if (view === 'month') {
      const startDate = `${columndId}-01`;
      const endDate = day(startDate).endOf('month').yyyymmdd();
      base.startAt = startDate;
      base.endAt = endDate;
    }

    if (view === 'quarter') {
      const split = columndId.split('-');
      const year = split[0];
      const quarter = split[1];
      const parsed = parseInt(quarter.at(1) || '1', 10);
      const month = parsed === 1 ? 1 : parsed === 2 ? 4 : parsed === 3 ? 7 : 10;
      let startDate = `${year}-${month < 10 ? `0${month}` : month}-01`;
      let endDate = day(startDate).add(2, 'month').endOf('month').yyyymmdd();

      if (day(startDate).isBefore(globalStartDate, 'day')) {
        startDate = globalStartDate;
      }
      if (day(endDate).isAfter(globalEndDate, 'day')) {
        endDate = globalEndDate;
      }

      base.startAt = startDate;
      base.endAt = endDate;
    }

    if (view === 'year') {
      const startDate = `${columndId}-01-01`;
      const endDate = day(startDate).endOf('year').yyyymmdd();
      base.startAt = startDate;
      base.endAt = endDate;
    }

    return () =>
      onOpenJournalEntries({
        filter: base,
      });
  }, [
    accountId,
    categoryId,
    columndId,
    isEmbed,
    onOpenJournalEntries,
    view,
    globalStartDate,
    globalEndDate,
  ]);

  return (
    <Base
      type={row.type}
      depth={depth}
      align="right"
      percentage={row.computed?.[columndId]?.total}
      onClick={onClick}
    >
      {children}
    </Base>
  );
};

export const ProfitAndLossTableCell = {
  Base,
  Number,
};
